import { useEffect, useState } from 'react';
import { tokens } from '../forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import { humanize } from '../../lib/stringCase';
import commitmentsService from '../../services/commitments.service';
import type { FollowupBrief } from '../../types/commitments';
import { readerLocale } from '../../i18n/language';

interface Props {
  caseId: string;
}

const TREND_LABEL: Record<string, string> = {
  improving: '↗ improving',
  stable: '→ stable',
  declining: '↘ declining',
};

/**
 * Pre-call brief panel rendered on the agent case-detail screen for
 * `deposit_followup_v1` cases. Shows everything the agent should know
 * before dialing the client: contact info, every overdue item with
 * gap, fulfillment-rate trend, monthly cap status, prior follow-up
 * notes, and rule-based talking points.
 *
 * Renders nothing for non-followup cases (the brief endpoint will
 * 400, so we hide the panel rather than show an error).
 */
export default function FollowupBriefPanel({ caseId }: Readonly<Props>): JSX.Element | null {
  const [brief, setBrief] = useState<FollowupBrief | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await commitmentsService.getFollowupBrief(caseId);
        if (!cancelled) setBrief(data);
      } catch (e) {
        if (!cancelled) setErr(getApiErrorMessage(e, 'Brief unavailable'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  if (err) {
    // Most common 400 case: not a follow-up case. Hide silently.
    return null;
  }
  if (!brief) {
    return (
      <div style={cardStyle}>
        <h3 style={h3Style}>Pre-call brief</h3>
        <p style={{ color: tokens.textMuted }}>Loading…</p>
      </div>
    );
  }

  const h = brief.deposit_history_6m;

  return (
    <div style={cardStyle}>
      <h3 style={h3Style}>Pre-call brief</h3>

      <CancellationCallout context={brief.cancellation_context} />
      <KycBanner kyc={brief.kyc_status ?? null} />
      <ComplianceFlagsBanner flags={brief.compliance_flags ?? null} />

      <section style={sectionStyle}>
        <h4 style={h4Style}>Contact</h4>
        <p style={{ margin: 0 }}>
          <strong>{brief.client.name}</strong>
          <br />
          <span style={{ color: tokens.textMuted, fontSize: 12 }}>
            {brief.client.email}
            {brief.client.phone ? ` · ${brief.client.phone}` : ''}
          </span>
        </p>
        {brief.last_contact ? (
          <p style={{ margin: '6px 0 0', fontSize: 12, color: tokens.textMuted }}>
            <strong>Last contact:</strong>{' '}
            {new Date(brief.last_contact.at).toLocaleString(readerLocale())} ·{' '}
            {humanize(brief.last_contact.kind)} ·{' '}
            <em>{humanize(brief.last_contact.outcome)}</em> · by{' '}
            {brief.last_contact.by_user_name}
          </p>
        ) : (
          <p style={{ margin: '6px 0 0', fontSize: 12, color: tokens.textMuted }}>
            <em>No prior contact on record.</em>
          </p>
        )}
      </section>

      <OpenCasesSection items={brief.open_cases ?? null} />
      <PendingAmendmentsSection items={brief.pending_amendments ?? null} />

      <section style={sectionStyle}>
        <h4 style={h4Style}>Overdue items ({brief.overdue_items.length})</h4>
        {brief.overdue_items.length === 0 ? (
          <p style={{ color: tokens.textMuted, fontSize: 12 }}>None.</p>
        ) : (
          <ul style={ulStyle}>
            {brief.overdue_items.map((it) => (
              <li key={it.expected_deposit_id} style={liStyle}>
                <strong>{it.product_name}</strong> — gap{' '}
                <span style={{ color: tokens.errorText }}>
                  {it.gap} {it.currency}
                </span>
                <br />
                <span style={{ color: tokens.textMuted, fontSize: 12 }}>
                  due {it.due_date} ({it.days_overdue}d overdue)
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section style={sectionStyle}>
        <h4 style={h4Style}>History (6m)</h4>
        <p style={{ margin: 0, fontSize: 13 }}>
          <strong>{h.fulfillment_rate_pct}%</strong> fulfillment over {h.total_count} items{' '}
          <span style={{ color: tokens.textMuted }}>· {TREND_LABEL[h.trend]}</span>
        </p>
        <p style={{ margin: '4px 0 0', color: tokens.textMuted, fontSize: 12 }}>
          fulfilled {h.fulfilled_count} · partial {h.partial_count} · missed {h.missed_count}
          {h.last_successful_deposit_at
            ? ` · last successful ${new Date(h.last_successful_deposit_at).toLocaleDateString(readerLocale())}`
            : ''}
        </p>
      </section>

      <section style={sectionStyle}>
        <h4 style={h4Style}>Active commitments</h4>
        {brief.active_commitments.length === 0 ? (
          <p style={{ color: tokens.textMuted, fontSize: 12 }}>None.</p>
        ) : (
          <ul style={ulStyle}>
            {brief.active_commitments.map((c) => (
              <li key={c.commitment_id} style={liStyle}>
                <strong>{c.product_name}</strong> · {c.cadence} · {formatMoney(c.amount, c.currency)}{' '}
                <span style={{ color: tokens.textMuted, fontSize: 12 }}>(v{c.config_version})</span>
                {c.status === 'paused' ? (
                  <span style={{ color: tokens.errorText }}> · paused</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {brief.monthly_cap_status.length ? (
        <section style={sectionStyle}>
          <h4 style={h4Style}>Monthly caps</h4>
          <ul style={ulStyle}>
            {brief.monthly_cap_status.map((cs) => (
              <li key={cs.product_id} style={liStyle}>
                <strong>{cs.product_name}</strong> — used {cs.used_this_month} of {cs.cap},{' '}
                <span style={{ color: tokens.textMuted }}>remaining {cs.remaining}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {brief.suggested_talking_points.length ? (
        <section style={sectionStyle}>
          <h4 style={h4Style}>Talking points</h4>
          <ul style={{ ...ulStyle, paddingLeft: 16, listStyle: 'disc' }}>
            {brief.suggested_talking_points.map((tp) => (
              <li key={tp} style={{ marginBottom: 6, fontSize: 13 }}>
                {tp}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {brief.previous_followup_summary ? (
        <section style={sectionStyle}>
          <h4 style={h4Style}>Previous follow-up</h4>
          <p style={{ margin: 0, fontSize: 12, color: tokens.textMuted }}>
            Resolved{' '}
            {brief.previous_followup_summary.completed_at
              ? new Date(brief.previous_followup_summary.completed_at).toLocaleDateString(readerLocale())
              : '—'}
          </p>
          {brief.previous_followup_summary.resolution_notes ? (
            <p style={{ margin: '4px 0 0', fontSize: 13 }}>
              {brief.previous_followup_summary.resolution_notes}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 16,
  color: tokens.textPrimary,
};
const h3Style: React.CSSProperties = { margin: '0 0 12px 0', fontSize: 14 };
const h4Style: React.CSSProperties = {
  margin: '0 0 6px 0',
  fontSize: 11,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: tokens.gold,
};
const sectionStyle: React.CSSProperties = {
  paddingBottom: 12,
  marginBottom: 12,
  borderBottom: `1px solid ${tokens.border}`,
};
const ulStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};
const liStyle: React.CSSProperties = {
  padding: '4px 0',
  fontSize: 13,
};

type CalloutProps = Readonly<{
  context: FollowupBrief['cancellation_context'];
}>;

function CancellationCallout({ context }: CalloutProps): JSX.Element | null {
  if (!context) return null;
  const isTermination = context.kind === 'termination';
  const term = context.termination;
  return (
    <section style={isTermination ? terminationCalloutStyle : changeCalloutStyle}>
      <h4 style={isTermination ? { ...h4Style, color: tokens.errorText } : h4Style}>
        {isTermination ? '⚠ Contract termination' : 'Renegotiation context'}
      </h4>
      <p style={{ margin: '0 0 6px', fontSize: 13 }}>
        {context.cancellation_reason
          ? `Agent's reason: ${context.cancellation_reason}`
          : 'Cancellation approved by manager.'}
      </p>
      {context.manager_notes ? (
        <p style={{ margin: '0 0 6px', fontSize: 12, color: tokens.textMuted }}>
          <strong>Manager notes:</strong> {context.manager_notes}
        </p>
      ) : null}
      {isTermination && term ? <TerminationFinancials t={term} /> : null}
    </section>
  );
}

type FinancialsProps = Readonly<{
  t: NonNullable<NonNullable<FollowupBrief['cancellation_context']>['termination']>;
}>;

function TerminationFinancials({ t }: FinancialsProps): JSX.Element {
  const retentionPctText = (Number.parseFloat(t.retention_pct) * 100).toFixed(2);
  return (
    <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
      <tbody>
        <tr>
          <td style={tdLabelStyle}>Balance at close</td>
          <td style={tdValueStyle}>
            {t.balance_at_close} {t.currency_code}
          </td>
        </tr>
        <tr>
          <td style={tdLabelStyle}>Retention ({retentionPctText}%)</td>
          <td style={tdValueStyle}>
            {t.retention_amount} {t.currency_code}
          </td>
        </tr>
        <tr>
          <td style={tdLabelStyle}>Scheduled return</td>
          <td style={{ ...tdValueStyle, color: tokens.errorText, fontWeight: 600 }}>
            {t.return_amount_scheduled} {t.currency_code} on {t.scheduled_return_date}
            <div style={{ color: tokens.textMuted, fontSize: 11, fontWeight: 400 }}>
              For you only. Never tell the client a day.
            </div>
          </td>
        </tr>
        {t.deposits_auto_rejected > 0 ? (
          <tr>
            <td style={tdLabelStyle}>Auto-rejected deposits</td>
            <td style={tdValueStyle}>{t.deposits_auto_rejected}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

const terminationCalloutStyle: React.CSSProperties = {
  background: tokens.errorBg,
  border: `2px solid ${tokens.errorBorder}`,
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
};
const changeCalloutStyle: React.CSSProperties = {
  background: tokens.infoBg,
  border: `1px solid ${tokens.infoBorder}`,
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
};
const tdLabelStyle: React.CSSProperties = {
  padding: '3px 0',
  color: tokens.textMuted,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const tdValueStyle: React.CSSProperties = {
  padding: '3px 0',
  textAlign: 'right',
  color: tokens.textPrimary,
};

// ─── Adjacent-signal sub-panels (extracted to keep the main render
//     function under the cognitive-complexity budget). Each one is a
//     pure presentational component over its own slice of the brief.

function KycBanner({
  kyc,
}: Readonly<{ kyc: FollowupBrief['kyc_status'] | null }>): JSX.Element | null {
  if (!kyc?.revalidation_required) return null;
  const isExpired = kyc.is_expired;
  // No hay `warning` token en la paleta — usamos `info` para
  // "expires soon" (señalético, no urgente) y `error` para "ya expiró"
  // (escalación inmediata).
  const style: React.CSSProperties = {
    background: isExpired ? tokens.errorBg : tokens.infoBg,
    border: `1px solid ${isExpired ? tokens.errorBorder : tokens.infoBorder}`,
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 12,
    fontSize: 13,
  };
  const label = isExpired
    ? `⚠ KYC expired ${Math.abs(kyc.days_to_expiry)}d ago`
    : `⚠ KYC expires in ${kyc.days_to_expiry}d`;
  return (
    <div style={style}>
      <strong style={{ color: isExpired ? tokens.errorText : tokens.infoText }}>
        {label}
      </strong>{' '}
      <span style={{ color: tokens.textMuted, fontSize: 12 }}>
        {isExpired
          ? '— ask them to upload the new document in their portal; the update request opens on the next daily run.'
          : '— tell them it expires soon; the portal will ask for a new one when it does.'}
      </span>
    </div>
  );
}

function ComplianceFlagsBanner({
  flags,
}: Readonly<{ flags: FollowupBrief['compliance_flags'] | null }>): JSX.Element | null {
  if (!flags || flags.length === 0) return null;
  return (
    <div
      style={{
        background: tokens.errorBg,
        border: `1px solid ${tokens.errorBorder}`,
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 12,
      }}
    >
      <h4 style={{ ...h4Style, color: tokens.errorText }}>Compliance flags</h4>
      <ul style={ulStyle}>
        {flags.map((f) => (
          <li
            key={`${f.kind}-${f.severity}-${f.message}`}
            style={{ ...liStyle, color: tokens.errorText }}
          >
            <strong>{f.severity.toUpperCase()}</strong> — {f.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function OpenCasesSection({
  items,
}: Readonly<{ items: FollowupBrief['open_cases'] | null }>): JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <section style={sectionStyle}>
      <h4 style={h4Style}>Open cases on this client ({items.length})</h4>
      <ul style={ulStyle}>
        {items.map((oc) => (
          <li key={oc.case_id} style={liStyle}>
            <strong>{oc.workflow_label}</strong>{' '}
            <span style={{ color: tokens.textMuted, fontSize: 12 }}>
              · {humanize(oc.current_state_slug)}
            </span>
            {oc.assigned_to_name ? (
              <span style={{ color: tokens.textMuted, fontSize: 12 }}>
                {' '}· assigned to {oc.assigned_to_name}
              </span>
            ) : null}
            {oc.is_spawned ? (
              <span style={{ color: tokens.gold, fontSize: 11, marginLeft: 6 }}>
                [spawned]
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PendingAmendmentsSection({
  items,
}: Readonly<{ items: FollowupBrief['pending_amendments'] | null }>): JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <section style={sectionStyle}>
      <h4 style={h4Style}>Pending contract amendments ({items.length})</h4>
      <ul style={ulStyle}>
        {items.map((pa) => (
          <li key={pa.signed_document_id} style={liStyle}>
            <strong>{pa.template_title}</strong> v{pa.template_version}{' '}
            <span style={{ color: tokens.textMuted, fontSize: 12 }}>
              · #{pa.contract_number} · {pa.status}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
